import { RefreshCw, Trash2, CheckCircle2, AlertTriangle, XCircle, HelpCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { EmailDomain } from "@/types/email-auth";

interface DomainCardProps {
  domain: EmailDomain;
  onCheck: (id: string) => void;
  onDelete: (id: string) => void;
  isChecking: boolean;
}

const statusConfig = {
  pass: { icon: CheckCircle2, label: "Pass", variant: "default" as const, className: "bg-green-100 text-green-800" },
  warn: { icon: AlertTriangle, label: "Warn", variant: "secondary" as const, className: "bg-yellow-100 text-yellow-800" },
  fail: { icon: XCircle, label: "Fail", variant: "destructive" as const, className: "bg-red-100 text-red-800" },
  unchecked: { icon: HelpCircle, label: "Unchecked", variant: "outline" as const, className: "bg-gray-100 text-gray-600" },
};

function StatusBadge({ type, status }: { type: string; status: string }) {
  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.unchecked;
  const Icon = config.icon;
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs font-medium text-muted-foreground uppercase">{type}</span>
      <Badge variant={config.variant} className={config.className}>
        <Icon className="mr-1 h-3 w-3" />
        {config.label}
      </Badge>
    </div>
  );
}

export function DomainCard({ domain, onCheck, onDelete, isChecking }: DomainCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-base">{domain.domain}</h3>
            {domain.last_checked && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Last checked: {new Date(domain.last_checked).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onCheck(domain.id)}
              disabled={isChecking}
              aria-label={`Check DNS records for ${domain.domain}`}
            >
              <RefreshCw className={`h-4 w-4 ${isChecking ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(domain.id)}
              aria-label={`Remove ${domain.domain}`}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-3">
          <StatusBadge type="SPF" status={domain.spf_status} />
          <StatusBadge type="DKIM" status={domain.dkim_status} />
          <StatusBadge type="DMARC" status={domain.dmarc_status} />
        </div>

        {domain.dmarc_policy && (
          <p className="mt-2 text-xs text-muted-foreground">
            DMARC policy: <span className="font-medium">{domain.dmarc_policy}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
